import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, TrendingUp, Award, Calendar, ChevronRight,
  Plus, BarChart3, Clock, CheckCircle2, Loader2,
  Copy, Check, LogOut, Hash, Settings
} from 'lucide-react';
import { useDashboard, useStudents, useRecentActivity, useProgressOverview } from '../../hooks/useTeacher';
import useAuthStore from '../../store/authStore';
import useAuth from '../../hooks/useAuth';

// Sample dashboard data (fallback)
const DASHBOARD_DATA = {
  school: {
    name: 'Green Valley Primary',
    class_code: '------',
    totalStudents: 0,
    activeStudents: 0,
  },
  stats: {
    totalActivities: 156,
    avgCompletion: 78,
    totalBadges: 45,
    currentWeek: 3,
  },
  recentActivity: [
    { id: 1, student: 'Wanjiku M.', action: 'Completed Week 3', time: '2 hours ago', points: 100 },
    { id: 2, student: 'Ochieng K.', action: 'Earned "First Sprout" badge', time: '3 hours ago', points: 50 },
    { id: 3, student: 'Amina H.', action: 'Completed activity', time: '5 hours ago', points: 25 },
    { id: 4, student: 'Kamau J.', action: 'Started Week 3', time: '1 day ago', points: 0 },
  ],
  topStudents: [
    { id: 1, name: 'Wanjiku M.', points: 2450, progress: 95 },
    { id: 2, name: 'Ochieng K.', points: 2320, progress: 90 },
    { id: 3, name: 'Amina H.', points: 2180, progress: 85 },
  ],
};

export default function Dashboard() {
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboard();
  const { data: studentsData, isLoading: studentsLoading } = useStudents();
  const { data: activityData, isLoading: activityLoading } = useRecentActivity(5);
  const { data: progressData, isLoading: progressLoading } = useProgressOverview();

  const [copied, setCopied] = useState(false);

  // Use API data if available, otherwise fallback to sample data
  const school = dashboardData?.school || user || DASHBOARD_DATA.school;
  const stats = dashboardData?.stats || DASHBOARD_DATA.stats;
  // activityData is { activities: [...], total: N } - extract the activities array
  const recentActivity = activityData?.activities || activityData || DASHBOARD_DATA.recentActivity;

  // Get class code from user data or dashboard data
  const classCode = user?.class_code || school?.class_code || DASHBOARD_DATA.school.class_code;

  // Use progressData or dashboard stats for actual values
  const classAvgCompletion = progressData?.class_avg_completion || stats.avgCompletion || 0;
  // Use total_badges from dashboard stats (already calculated from student_badges table)
  const totalBadges = stats.totalBadges || stats.total_badges ||
    studentsData?.reduce((sum, student) => sum + (student.badges_earned || 0), 0) || 0;
  // Calculate average current week from students
  const currentWeek = studentsData?.length > 0
    ? Math.round(studentsData.reduce((sum, s) => sum + (s.current_week || 1), 0) / studentsData.length)
    : stats.currentWeek || 1;

  // Calculate top students from students data or use sample
  const topStudents = studentsData
    ? [...studentsData]
        .sort((a, b) => (b.total_points || b.points || 0) - (a.total_points || a.points || 0))
        .slice(0, 3)
        .map((s) => ({
          id: s.id,
          name: s.name,
          points: s.total_points || s.points || 0,
          progress: Math.round(((s.current_week || s.week || 1) / 36) * 100),
        }))
    : DASHBOARD_DATA.topStudents;

  const totalStudents = studentsData?.length || school?.totalStudents || DASHBOARD_DATA.school.totalStudents;

  const isLoading = dashboardLoading || studentsLoading || progressLoading;

  const handleCopyCode = async () => {
    if (classCode && classCode !== '------') {
      try {
        await navigator.clipboard.writeText(classCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleLogout = () => {
    logout();
  };

  const statCards = [
    { label: 'Total Students', value: totalStudents, icon: Users, color: 'bg-blue-500' },
    { label: 'Avg. Completion', value: classAvgCompletion, icon: TrendingUp, color: 'bg-leaf-500' },
    { label: 'Badges Earned', value: totalBadges, icon: Award, color: 'bg-purple-500' },
    { label: 'Current Week', value: currentWeek, icon: Calendar, color: 'bg-sunshine-500' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-leaf-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Teacher Dashboard</h1>
              <p className="text-gray-600">{school?.school_name || school?.name || 'Your School'}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/teacher/students"
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={20} />
                Add Student
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-red-600 transition-colors"
                title="Sign out"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Class Code Card - Prominent Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-leaf-500 to-leaf-600 rounded-2xl shadow-lg p-6 mb-8 text-white"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Hash size={20} />
                <span className="font-medium">Your Class Code</span>
              </div>
              <p className="text-leaf-100 text-sm mb-3">
                Share this code with your students so they can sign in
              </p>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold tracking-[0.2em] font-mono">
                  {classCode}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                  title="Copy class code"
                >
                  {copied ? (
                    <Check size={20} />
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
              </div>
              {copied && (
                <p className="text-sm text-leaf-100 mt-2">
                  Copied to clipboard!
                </p>
              )}
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-4xl font-bold">{totalStudents}</p>
              <p className="text-leaf-100 text-sm">Students enrolled</p>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-sm"
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <Link to="/teacher/progress" className="text-leaf-600 text-sm font-medium hover:text-leaf-700">
                  View All
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                    <div className="w-10 h-10 rounded-full bg-leaf-100 flex items-center justify-center">
                      <CheckCircle2 size={20} className="text-leaf-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{activity.student}</p>
                      <p className="text-sm text-gray-500">{activity.action}</p>
                    </div>
                    <div className="text-right">
                      {activity.points > 0 && (
                        <p className="text-sm font-semibold text-leaf-600">+{activity.points} pts</p>
                      )}
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={12} />
                        {activity.time}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No recent activity yet. Add students to get started!
                </div>
              )}
            </div>
          </motion.div>

          {/* Top Students */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-sm"
          >
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Top Students</h2>
                <Link to="/teacher/progress" className="text-leaf-600 text-sm font-medium hover:text-leaf-700">
                  View All
                </Link>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {topStudents.length > 0 ? (
                topStudents.map((student, index) => (
                  <div key={student.id} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                      ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        'bg-orange-100 text-orange-700'}`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{student.name}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-leaf-500 rounded-full"
                            style={{ width: `${student.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{student.progress}%</span>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-sunshine-600">{student.points} pts</p>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  No students yet
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-white rounded-xl shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/teacher/students"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-100 hover:border-leaf-200 hover:bg-leaf-50 transition-colors"
            >
              <Users size={24} className="text-leaf-600" />
              <div>
                <p className="font-medium">Manage Students</p>
                <p className="text-sm text-gray-500">Add, edit, view PINs</p>
              </div>
              <ChevronRight size={20} className="ml-auto text-gray-400" />
            </Link>
            <Link
              to="/teacher/progress"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-100 hover:border-leaf-200 hover:bg-leaf-50 transition-colors"
            >
              <BarChart3 size={24} className="text-blue-600" />
              <div>
                <p className="font-medium">View Progress</p>
                <p className="text-sm text-gray-500">Class reports</p>
              </div>
              <ChevronRight size={20} className="ml-auto text-gray-400" />
            </Link>
            <Link
              to="/teacher/certificates"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-100 hover:border-leaf-200 hover:bg-leaf-50 transition-colors"
            >
              <Award size={24} className="text-purple-600" />
              <div>
                <p className="font-medium">Certificates</p>
                <p className="text-sm text-gray-500">Generate & print</p>
              </div>
              <ChevronRight size={20} className="ml-auto text-gray-400" />
            </Link>
            <Link
              to="/student-login"
              className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-100 hover:border-leaf-200 hover:bg-leaf-50 transition-colors"
            >
              <Calendar size={24} className="text-sunshine-600" />
              <div>
                <p className="font-medium">Student Login</p>
                <p className="text-sm text-gray-500">Preview sign-in</p>
              </div>
              <ChevronRight size={20} className="ml-auto text-gray-400" />
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
